// typescript
import React from "react";
import { getThemePresentation } from "@/main/navigation/ThemeRuntime";
import { useThemeState } from "@/main/navigation/useThemeState";
import {createReactMount} from "@/main/react/mountContract";
import {Toaster} from "@/components/ui/sonner";
import {useIsMobile} from "@/hooks/use-mobile";
import {useToastFocusPause} from "@/main/react/header/toastFocusPause";


// Shape + interaction contract shared by the toast's action and cancel buttons. sonner hardcodes
// `border-radius: 4px`, `height: 24px`, `padding: 0 8px`, `font-size: 12px` and `border: none` at a
// specificity a plain utility cannot reach, so each of those is restated with `!`. The focus ring and
// the coarse-pointer 44px floor are additive and need no importance.
const TOAST_BUTTON_BASE =
    "!rounded-full !h-8 !px-3 !text-sm font-medium !border !border-border transition-colors " +
    "outline-none focus-visible:border-ring focus-visible:ring-ring/70 focus-visible:ring-[3px] " +
    "touch-manipulation [@media(pointer:coarse)]:min-h-11 [@media(pointer:coarse)]:min-w-11";

/** Desktop toasts clear the editor's sticky bottom action bar and the Create FAB. Exported for the spec. */
export const DESKTOP_TOAST_BOTTOM_OFFSET_PX = 96;

/**
 * FI-6208 (MESSAGES-187, A16 a): the desktop offset honours the same `--toast-min-bottom` floor as the phone one, so a
 * mounted chat composer (`useComposerToastFloor`, lazy chat chunk) lifts toasts off the newest message. Unset, it is
 * the 96 px above. A literal, not built from the two constants, so this eager module grows by one string (rule 27).
 */
export const DESKTOP_TOAST_BOTTOM_OFFSET = "max(96px, var(--toast-min-bottom, 0px))";

/**
 * FI-1281 (r72 A8): phone toasts sit bottom-center, lifted above the bottom bar and the home-indicator safe area.
 * A constant reserve rather than a read of the bar's visibility, so this eager module imports nothing new: where the
 * bar is hidden (a post page, the composer) the same band is the docked action bar's or simply empty. Exported for
 * the spec.
 */
export const PHONE_TOAST_BOTTOM_OFFSET =
    "max(calc(var(--mobile-bottom-nav-height) + env(safe-area-inset-bottom) + 0.5rem), var(--toast-min-bottom, 0px))";

/**
 * A floor a bottom-chrome control may publish on `<html>` while it is mounted, so phone toasts stack ABOVE it rather
 * than over it (devo gate d9e4a24f0d: the "Published" toast covered the reply session's Next post pill, so the first
 * tap hit the toast). Unset, the `max()` above reduces to the bar reserve. Owners: `ReplySessionEntryBar` (phone),
 * `useComposerToastFloor` (a chat composer, FI-6208; the phone one too since FI-6863).
 */
export const TOAST_MIN_BOTTOM_PROPERTY = "--toast-min-bottom";

export function ToasterReactComponent() {
    const themeState = useThemeState();
    // `CREATE-PUBLISH-151` (V48 a, sitewide). Top-center on desktop hung the toast below the header, over the
    // header search and the first content line (a draft's title, by about 6 px). Desktop toasts now sit in
    // the bottom-right corner, clear of both.
    //
    // FI-1281 (r72 A8, 2026-10-07): phones moved from top-center to bottom-center. SV-157 removed the phone top
    // bar, so a top toast landed over page content where the header used to be (and, before devo gate
    // a8f8756422 lowered it, on the composer's top workflow bar over Publish). Bottom-center sits where a phone's
    // chrome now is, above the bottom bar and the safe area. Both props carry the offset: sonner switches to
    // `mobileOffset` only at <= 600px, while our phone layout runs to 767px.
    // A toast that passes its own `position` still overrides this default.
    //
    // Lifted off the bottom edge on desktop (devo gate f8d2045b59, 2026-09-29; operator call "bottom-right,
    // lifted"). At sonner's default 24px the corner toast sat ON the editor's sticky bottom action bar, over
    // Publish, and on the desktop Create FAB (16px + 56px). sonner pauses a toast while the pointer rests on
    // it, so a member reaching for Publish after an image upload held the toast in place. 96px clears both.
    const isMobile = useIsMobile();
    // U35 (FI-3560): keyboard focus inside the toaster pauses every toast's timer, as hover does. The 4s default below
    // is for informational toasts; one carrying Undo or Retry passes `UNDO_TOAST_MS` (`toastTiming.ts`).
    useToastFocusPause();

    return (
        <Toaster
            theme={getThemePresentation(themeState.preference)}
            position={isMobile ? "bottom-center" : "bottom-right"}
            offset={{bottom: isMobile ? PHONE_TOAST_BOTTOM_OFFSET : DESKTOP_TOAST_BOTTOM_OFFSET}}
            mobileOffset={isMobile ? {bottom: PHONE_TOAST_BOTTOM_OFFSET} : undefined}
            toastOptions={{
                duration: 4000,
                classNames: {
                    // r106 D26: the toast is on the overlay MATERIAL. Its fill is sonner's `--normal-bg` (sonner.tsx, the
                    // material recipe), so no `!bg-…` here: a forced solid fill would hide it. `material-blur` is the blur.
                    toast: "app-toast material-blur !text-[var(--text-main)] !border !border-[var(--line-color)] !rounded-md !p-3 !shadow-lg",
                    title: "!font-semibold !text-[var(--text-main)]",
                    description: "!text-[var(--text-muted)]",
                    // sonner's own `[data-sonner-toast][data-styled='true'] [data-button]` rule wins
                    // over an unprefixed utility on specificity, and it INVERTS the toast
                    // (`background: var(--normal-text)`). Styling only the text leaves a near-black
                    // pill behind it, so every property sonner sets is restated with `!`.
                    //
                    // FILLED, not the outline pill it was until 2026-08-06: as `!bg-card
                    // !text-primary` this measured 2.99:1 in the light stateful lane, under AA's 4.5
                    // for 14px text. `--primary` / `--primary-foreground` is the one pair that cannot
                    // drift into that, because each half is pinned to the opposite end of its own
                    // theme (`--primary-foreground` is `--background1`) — 6.6:1 light, 6.8:1 dark,
                    // and it stays true if the accent hue is re-toned. The toast's one action is
                    // also the recovery affordance, so reading as primary is correct, not merely
                    // convenient. Guarded by draft-loss-prevention.spec.mjs's computed-contrast
                    // assertion, which is what caught the 2.99.
                    // Border goes transparent rather than away: the 1px is what keeps this pill the
                    // same box as the outline cancel beside it, so the two stay aligned.
                    actionButton: TOAST_BUTTON_BASE + " !bg-primary !text-primary-foreground !border-transparent hover:!bg-[var(--primary-action-hover)]",
                    // `--neutral-text` rather than `--text-muted`: on `--card` the latter is 4.4:1 in
                    // light, just under AA. A dismiss control is quieter than the action, not fainter
                    // than legible.
                    cancelButton: TOAST_BUTTON_BASE + " !bg-card !text-neutral-text hover:!bg-muted",
                    // `CREATE-PUBLISH-160`: sonner paints its close button as a 20px disc, and a persistent toast
                    // (`duration: Infinity`, `closeButton: true`) has no other exit, so on a coarse pointer it
                    // gets the 44px hit expander. The disc keeps its painted size (the expander is a transparent
                    // `::after`); sonner's own `::after` is on the TOAST, never on this button, so nothing collides
                    // (the `components/ui/tabs.tsx` hazard). `!absolute` restates sonner's own positioning so the
                    // expander's containing block is declared on the same element (`tapTargets.contract.test.tsx`).
                    closeButton: "!text-[var(--text-muted)] !absolute touch-hit-44"
                }
            }}
        />    );
}


export const mountToasterReactComponent = createReactMount({
    name: "mountToasterReactComponent",
    render: () => <ToasterReactComponent/>,
});
